// Push notifications, backed by the Go backend's github.com/kuetix/std-push
// wiring via the same /api-auth proxy prefix the other API modules use.
//
// - POST   /push/subscribe     register a Web Push subscription (flat body)
// - DELETE /push/subscribe     drop one subscription
// - GET    /push/preferences   {enabled, messages, signals, reports, mentions, email, preview,
//                                quietHoursEnabled, quietHoursStart, quietHoursEnd, quietHoursTimezone}
// - POST   /push/preferences   update any subset of those fields
//
// Mutations need the double-submit CSRF header (same as reportApi); the
// session rides the httpOnly cookie via credentials: 'include'.
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

export interface PushPreferences {
  enabled: boolean;
  messages: boolean;
  signals: boolean;
  reports: boolean;
  // Defaults true, like messages/signals/reports: being @mentioned by name
  // gets its own notification, independent of the other type toggles.
  mentions: boolean;
  messagesMode: 'off' | 'immediate' | 'digest';
  signalsMode: 'off' | 'immediate' | 'digest';
  reportsMode: 'off' | 'immediate' | 'digest';
  mentionsMode: 'off' | 'immediate' | 'digest';
  // Opt-in (defaults false): also send missed-activity notifications to
  // the account's verified email address.
  email: boolean;
  // Opt-in (defaults false): show the message text / signal value inside
  // the notification itself. Never affects whether a notification is sent,
  // only what it contains.
  preview: boolean;
  // Opt-in (defaults false): suppress every push during
  // [quietHoursStart, quietHoursEnd), evaluated in quietHoursTimezone. The
  // underlying activity is unaffected - same as a muted channel, this only
  // withholds the OS notification.
  quietHoursEnabled: boolean;
  /** "HH:MM", 24h. Defaults "22:00". May wrap midnight (e.g. 22:00 -> 08:00). */
  quietHoursStart: string;
  /** "HH:MM", 24h. Defaults "08:00". */
  quietHoursEnd: string;
  /** IANA zone name, e.g. "America/New_York". Defaults "UTC". */
  quietHoursTimezone: string;
}

async function unwrap<T>(response: Response): Promise<T> {
  let parsed: any = null;
  try {
    parsed = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = parsed?.data ?? parsed;
  if (response.ok && data && typeof data === 'object' && !('error' in data)) {
    return data as T;
  }
  const message =
    (data && typeof data === 'object' && data.error) || `Request failed (${response.status})`;
  throw new Error(String(message));
}

function mutHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return headers;
}

export interface WebSubscribePayload {
  platform: 'web';
  endpoint: string;
  p256dh: string;
  auth: string;
}

export async function subscribeWeb(payload: WebSubscribePayload): Promise<void> {
  const response = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: mutHeaders(),
    body: JSON.stringify(payload),
  });
  await unwrap<{ subscribed: boolean }>(response);
}

export async function unsubscribeWeb(endpoint: string): Promise<void> {
  const response = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'DELETE',
    credentials: 'include',
    headers: mutHeaders(),
    body: JSON.stringify({ endpoint }),
  });
  await unwrap<{ unsubscribed: boolean }>(response);
}

export interface PushSubscriptionInfo {
  id: string;
  platform: 'web' | 'ios';
  label: string;
  createdAt: string;
}

export interface PushSubscriptionsResult {
  count: number;
  web: number;
  ios: number;
  subscriptions: PushSubscriptionInfo[];
}

/** The caller's registered push targets (redacted - no keys/tokens). */
export async function fetchPushSubscriptions(): Promise<PushSubscriptionsResult> {
  const response = await fetch(`${API_BASE}/push/subscriptions`, { credentials: 'include' });
  return unwrap<PushSubscriptionsResult>(response);
}

export interface PushTestResult {
  queued: boolean;
  type: string;
  channelId: string;
  deviceCount: number;
  web: number;
  ios: number;
  allowed: boolean;
  note: string;
}

/**
 * Send yourself a test push through the real pipeline (cue:missed -> decide
 * -> scheduler). Preferences and the burst window still apply, so the
 * result reports whether your prefs allow this type and how many devices
 * are registered.
 */
export async function sendTestPush(
  body: { channelId?: string; type?: 'message' | 'signal' | 'report' } = {}
): Promise<PushTestResult> {
  const response = await fetch(`${API_BASE}/push/test`, {
    method: 'POST',
    credentials: 'include',
    headers: mutHeaders(),
    body: JSON.stringify(body),
  });
  return unwrap<PushTestResult>(response);
}

export async function fetchPushPreferences(): Promise<PushPreferences> {
  const response = await fetch(`${API_BASE}/push/preferences`, { credentials: 'include' });
  return unwrap<PushPreferences>(response);
}

export async function updatePushPreferences(
  patch: Partial<PushPreferences>
): Promise<PushPreferences> {
  const body: Record<string, string> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (typeof v === 'boolean') body[k] = v ? 'true' : 'false';
    else if (typeof v === 'string') body[k] = v;
  }
  const response = await fetch(`${API_BASE}/push/preferences`, {
    method: 'POST',
    credentials: 'include',
    headers: mutHeaders(),
    body: JSON.stringify(body),
  });
  return unwrap<PushPreferences>(response);
}
